/**
 * /admin/settings — admin-only UI over /api/settings.
 *
 * Shows every catalogued key with its effective value and source
 * (default | global). Editing a key flips its source to 'global' on save.
 */

import { type FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiError, api } from '../lib/api';
import { Badge, Banner } from '../components/brand';

interface SettingsResponse {
  values: Record<string, unknown>;
  sources: Record<string, 'default' | 'global'>;
}

const GROUPS: { title: string; keys: string[] }[] = [
  {
    title: 'Upload / download limits',
    keys: [
      'upload.max_file_size_bytes',
      'upload.max_total_size_bytes',
      'upload.part_size_bytes',
      'upload.max_parts_per_file',
      'upload.max_concurrent_parts',
      'upload.allowed_mime_types',
      'upload.blocked_extensions',
      'upload.session_ttl_seconds',
      'download.batch_max_files',
      'download.batch_max_total_size_bytes',
    ],
  },
  {
    title: 'Sharing',
    keys: ['share.presign_max_expiry_seconds', 'share.allow_long_lived', 'share.allow_public_url'],
  },
  {
    title: 'Maintenance',
    keys: ['maintenance_mode'],
  },
  {
    title: 'Branding',
    keys: [
      'branding.app_name',
      'branding.primary_color',
      'branding.footer_text',
      'i18n.default_locale',
      'theme.default',
    ],
  },
];

export function AdminSettings() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['settings', 'admin'],
    queryFn: () => api.get<SettingsResponse>('/api/settings'),
  });
  const [pending, setPending] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch<SettingsResponse>('/api/settings', body),
    onSuccess: () => {
      setPending({});
      qc.invalidateQueries({ queryKey: ['settings', 'admin'] });
    },
  });

  const reset = useMutation({
    mutationFn: (keys: string[]) => api.post<SettingsResponse>('/api/settings/reset', { keys }),
    onSuccess: () => {
      setPending({});
      qc.invalidateQueries({ queryKey: ['settings', 'admin'] });
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const body: Record<string, unknown> = {};
    for (const [k, raw] of Object.entries(pending)) {
      const initial = data?.values[k];
      let parsed: unknown = raw;
      try {
        if (typeof initial === 'number') parsed = Number(raw);
        else if (typeof initial === 'boolean') parsed = raw === 'true';
        else if (Array.isArray(initial)) parsed = JSON.parse(raw);
      } catch {
        save.mutate(body);
        return;
      }
      body[k] = parsed;
    }
    if (Object.keys(body).length === 0) return;
    save.mutate(body);
  }

  if (isLoading) {
    return (
      <p className="muted" style={{ padding: 24 }}>
        Loading…
      </p>
    );
  }
  if (error) {
    return (
      <Banner variant="danger" code={(error as ApiError).code ?? 'ERROR'}>
        {(error as Error).message}
      </Banner>
    );
  }
  if (!data) return null;

  const dirtyCount = Object.keys(pending).length;

  return (
    <form onSubmit={submit}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}
      >
        <div>
          <h1 className="h-page">Settings</h1>
          <p className="muted" style={{ marginTop: 6 }}>
            Tenant-wide configuration. Edits land in the global override store.
          </p>
        </div>
        <button
          type="submit"
          className="bm-btn bm-btn-primary"
          disabled={dirtyCount === 0 || save.isPending}
        >
          {save.isPending ? 'Saving…' : `Save${dirtyCount > 0 ? ` (${dirtyCount})` : ''}`}
        </button>
      </div>

      {save.error && (
        <div style={{ marginBottom: 16 }}>
          <Banner variant="danger" code={(save.error as ApiError).code ?? 'ERROR'}>
            {(save.error as Error).message}
          </Banner>
        </div>
      )}
      {reset.error && (
        <div style={{ marginBottom: 16 }}>
          <Banner variant="danger" code={(reset.error as ApiError).code ?? 'ERROR'}>
            {(reset.error as Error).message}
          </Banner>
        </div>
      )}

      <div className="bm-col bm-gap-16">
        {GROUPS.map((group) => (
          <section key={group.title} className="bm-card" style={{ padding: 0 }}>
            <div className="bm-card-header">
              <span className="h-eyebrow">{group.title}</span>
            </div>
            <div style={{ padding: '6px 0' }}>
              {group.keys.map((k) => {
                const initial = data.values[k];
                const source = data.sources[k] ?? 'default';
                const currentRaw = renderRaw(initial);
                const pendingRaw = pending[k];
                const displayValue = pendingRaw ?? currentRaw;
                return (
                  <div
                    key={k}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.2fr 2fr auto',
                      alignItems: 'center',
                      gap: 14,
                      padding: '12px 20px',
                      borderTop: '1px solid var(--bm-border-soft)',
                    }}
                  >
                    <div className="bm-col bm-gap-4">
                      <span className="mono" style={{ fontSize: 12.5, color: 'var(--bm-text)' }}>
                        {k}
                      </span>
                      <span>
                        <Badge variant={source === 'global' ? 'success' : 'neutral'}>
                          {source}
                        </Badge>
                      </span>
                    </div>
                    <input
                      type="text"
                      className="bm-input mono"
                      value={displayValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPending((p) => {
                          const next = { ...p };
                          if (v === currentRaw) delete next[k];
                          else next[k] = v;
                          return next;
                        });
                      }}
                    />
                    <button
                      type="button"
                      className="bm-btn bm-btn-ghost bm-btn-sm"
                      disabled={source !== 'global' || reset.isPending}
                      onClick={() => reset.mutate([k])}
                    >
                      Reset
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </form>
  );
}

function renderRaw(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}
